// (c) chaehongee. Confidential and proprietary.

import i18n from '../i18n'

export interface HttpError extends Error {
    status: number
    data?: unknown
}

/**
 * Pull a human-readable reason out of a FastAPI error body.
 *
 * Without this the thrown Error reads "HTTP 422" and the actual explanation —
 * which files failed, what to do about it — stays buried in `err.data` where
 * no caller looks. `detail` is a string for HTTPException and a list of
 * objects for request-validation errors.
 */
function errorMessageFrom(status: number, data: unknown): string {
    const fallback = `HTTP ${status}`
    if (!data || typeof data !== 'object') return fallback

    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (Array.isArray(detail)) {
        const parts = detail
            .map((d) =>
                d && typeof d === 'object' && typeof (d as { msg?: unknown }).msg === 'string'
                    ? (d as { msg: string }).msg
                    : null
            )
            .filter((m): m is string => Boolean(m))
        if (parts.length) return parts.join('; ')
    }
    return fallback
}

const DEFAULT_TIMEOUT_MS = 30000

export function applyBackendProfilingHeader(
    headers: Headers,
    enabled?: boolean // DEBUG:set to true to enable profiling (default is false)
): void {
    if (enabled) { 
        headers.set('X-Knoll-Profile', '1')
    }
}

function getLocaleHeader(): string {
    try {
        return i18n.language || 'en'
    } catch {
        return 'en'
    }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            const err = new Error('Request timed out') as HttpError
            err.status = 408
            reject(err)
        }, ms)

        promise
            .then((value) => {
                clearTimeout(timer)
                resolve(value)
            })
            .catch((error) => {
                clearTimeout(timer)
                reject(error)
            })
    })
}

import * as Sentry from '@sentry/react'
import { getEnv } from './env'
import { getSupabaseClient } from './supabase'
import { authStateManager } from './authState'
import { telemetry } from './telemetry'

/**
 * Token refresh state management
 * Ensures only one refresh happens at a time, queuing concurrent requests
 */
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

function onRefreshed(token: string) {
    refreshSubscribers.forEach((callback) => callback(token))
    refreshSubscribers = []
}

function addRefreshSubscriber(callback: (token: string) => void) {
    refreshSubscribers.push(callback)
}

/**
 * Attempt to refresh the token and retry the original request.
 *
 * If multiple requests fail with 401 simultaneously, only one refresh is triggered
 * and all other requests are queued until the refresh completes.
 *
 * @param originalRequest Original request info
 * @param originalInit Original request init options
 * @returns Response from retried request
 * @throws Error if refresh fails or retry still returns 401
 */
async function refreshTokenAndRetry(
    originalRequest: RequestInfo,
    originalInit: RequestInit
): Promise<Response> {
    if (!isRefreshing) {
        // First request to trigger refresh
        isRefreshing = true

        try {
            console.log('Attempting token refresh after 401 error')
            const supabase = getSupabaseClient()
            const { data, error } = await supabase.auth.refreshSession()

            if (error || !data.session) {
                // Refresh failed - session truly expired
                console.error('Token refresh failed:', error)
                isRefreshing = false

                telemetry.trackEvent('auth_token_refresh', {
                    success: false,
                    reason: '401_error',
                    error: error?.message,
                })

                // Dispatch custom event to trigger login redirect
                window.dispatchEvent(
                    new CustomEvent('auth:session-expired', {
                        detail: { reason: 'refresh_failed', error: error?.message },
                    })
                )

                throw new Error('Session expired. Please log in again.')
            }

            const newToken = data.session.access_token
            console.log('Token refresh successful, retrying original request')

            telemetry.trackEvent('auth_token_refresh', {
                success: true,
                reason: '401_error',
            })

            isRefreshing = false
            onRefreshed(newToken) // Notify queued requests

            // Retry original request with new token
            const headers = new Headers(originalInit.headers)
            headers.set('Authorization', `Bearer ${newToken}`)

            return fetch(originalRequest, {
                ...originalInit,
                headers,
            })
        } catch (error) {
            isRefreshing = false
            throw error
        }
    } else {
        // Token refresh already in progress - queue this request
        console.log('Token refresh in progress, queueing request')

        return new Promise((resolve, reject) => {
            addRefreshSubscriber((token: string) => {
                const headers = new Headers(originalInit.headers)
                headers.set('Authorization', `Bearer ${token}`)

                fetch(originalRequest, {
                    ...originalInit,
                    headers,
                })
                    .then(resolve)
                    .catch(reject)
            })
        })
    }
}

export async function http<T>(
    input: RequestInfo,
    init?: RequestInit & { timeoutMs?: number; profile?: boolean; skipAuthRetry?: boolean }
): Promise<T> {
    const controller = new AbortController()
    const timeoutMs = init?.timeoutMs ?? DEFAULT_TIMEOUT_MS
    const headers = new Headers(init?.headers)
    // Only set Content-Type when we actually send a non-FormData body
    if (init?.body && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json')
    }
    headers.set('Accept', 'application/json')

    applyBackendProfilingHeader(headers, init?.profile)

    // Add locale header for backend localization
    headers.set('X-Knoll-Locale', getLocaleHeader())

    // Add JWT token if user is authenticated
    // PROACTIVE: Ensure token is fresh before making request
    try {
        const token = await authStateManager.ensureFreshToken()
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
    } catch (error) {
        // Fall back to current session token
        console.warn('Failed to ensure fresh token, falling back to current session:', error)
        try {
            const supabase = getSupabaseClient()
            const {
                data: { session },
            } = await supabase.auth.getSession()
            if (session?.access_token) {
                headers.set('Authorization', `Bearer ${session.access_token}`)
            }
        } catch (err) {
            console.warn('Failed to get session token:', err)
        }
    }

    const request = fetch(input, {
        ...init,
        headers,
        signal: controller.signal,
    })

    const response = await withTimeout(request, timeoutMs)

    // Handle 401 Unauthorized - try token refresh and retry
    // Only attempt refresh if we actually sent an auth token (i.e., user was signed in).
    // Unauthenticated users get 401 naturally — no session to "expire".
    if (response.status === 401 && !init?.skipAuthRetry && headers.has('Authorization')) {
        console.log('Received 401 error, attempting token refresh and retry')

        try {
            const retryResponse = await refreshTokenAndRetry(input, {
                ...init,
                headers,
                signal: controller.signal,
            })

            // Check if retry succeeded
            if (retryResponse.ok) {
                // Success after retry - parse and return response
                const contentType = retryResponse.headers.get('content-type') || ''
                const contentLength = retryResponse.headers.get('content-length')

                if (retryResponse.status === 204 || contentLength === '0') {
                    return undefined as unknown as T
                }
                if (contentType.includes('application/json')) {
                    try {
                        return (await retryResponse.json()) as unknown as T
                    } catch {
                        return undefined as unknown as T
                    }
                }
                return (await retryResponse.text()) as unknown as T
            } else {
                // Still failed after retry - track and throw error
                console.error('Request still failed after token refresh:', retryResponse.status)

                telemetry.trackMetric('auth_failures_after_retry', 1, {
                    endpoint: typeof input === 'string' ? input : input.url,
                    status: retryResponse.status,
                })

                let data: unknown = undefined
                try {
                    data = await retryResponse.json()
                } catch {
                    // ignore json parse error
                }
                const err = new Error(
                    errorMessageFrom(retryResponse.status, data)
                ) as HttpError
                err.status = retryResponse.status
                err.data = data
                throw err
            }
        } catch (refreshError) {
            console.error('Token refresh and retry failed:', refreshError)
            // Fall through to original error handling
        }
    }

    if (!response.ok) {
        let data: unknown = undefined
        try {
            data = await (response as Response).json()
        } catch {
            // ignore json parse error
        }
        const err = new Error(errorMessageFrom(response.status, data)) as HttpError
        err.status = (response as Response).status
        err.data = data
        if (err.status >= 500) {
            Sentry.captureException(err, {
                tags: { http_status: err.status },
                extra: { endpoint: typeof input === 'string' ? input : (input as Request).url },
            })
        }
        throw err
    }

    const res = response as Response
    const contentType = res.headers.get('content-type') || ''
    const contentLength = res.headers.get('content-length')
    // Handle no-content responses gracefully (e.g., 204 or empty body with JSON content-type)
    if (res.status === 204 || contentLength === '0') {
        return undefined as unknown as T
    }
    if (contentType.includes('application/json')) {
        try {
            return (await res.json()) as unknown as T
        } catch {
            // Some servers send application/json with empty body; treat as void
            return undefined as unknown as T
        }
    }
    // Fallback: read as text; may be empty string for no-body responses
    return (await res.text()) as unknown as T
}

export function buildApiUrl(path: string): string {
    const { apiBaseUrl } = getEnv()
    const base = apiBaseUrl.replace(/\/$/, '')
    const trimmedPath = path.startsWith('/') ? path.slice(1) : path
    // Frontend expects full base url to already include /api/v1 when needed
    return `${base}/${trimmedPath}`
}